import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateVehicleInput, VehicleDto } from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';

type VehicleForm = {
  plate: string;
  brand: string;
  model: string;
  color: string;
  type: NonNullable<CreateVehicleInput['type']>;
  customerId: string;
};

const emptyForm: VehicleForm = {
  plate: '',
  brand: '',
  model: '',
  color: '',
  type: 'CAR',
  customerId: '',
};

export function VehiclesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('vehicles:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('vehicles:write'));
  const canReadCustomers = useAuthStore((s) => s.hasPermission('customers:read'));

  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<VehicleForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const vehicles = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => api.listVehicles(),
    enabled: canRead,
  });

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.listCustomers(),
    enabled: dialog && canReadCustomers,
  });

  const closeDialog = () => {
    setDialog(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
  };

  const openCreate = () => {
    setFormError(null);
    setEditingId(null);
    setForm(emptyForm);
    setDialog(true);
  };

  const openEdit = (v: VehicleDto) => {
    setFormError(null);
    setEditingId(v.id);
    setForm({
      plate: v.plate,
      brand: v.brand ?? '',
      model: v.model ?? '',
      color: v.color ?? '',
      type: (v.type as NonNullable<CreateVehicleInput['type']>) || 'CAR',
      customerId: v.primaryCustomerId ?? '',
    });
    setDialog(true);
  };

  const saveVehicle = useMutation({
    mutationFn: async () => {
      const payload = {
        plate: form.plate.trim(),
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
        color: form.color.trim() || null,
        type: form.type,
      };
      if (editingId) {
        return api.updateVehicle(editingId, payload);
      }
      return api.createVehicle({
        ...payload,
        ...(form.customerId ? { customerId: form.customerId, isPrimary: true } : {}),
      });
    },
    onSuccess: async () => {
      closeDialog();
      await qc.invalidateQueries({ queryKey: ['vehicles'] });
      await qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const deleteVehicle = useMutation({
    mutationFn: (id: string) => api.deleteVehicle(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vehicles'] });
      await qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('vehicles.forbidden')}</div>;
  }

  const rows = vehicles.data?.data ?? [];
  const columns = [
    t('vehicles.plate'),
    t('vehicles.brand'),
    t('vehicles.model'),
    t('vehicles.color'),
    t('vehicles.type'),
    t('vehicles.customers'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
      <PageHeader
        actions={
          canWrite ? (
            <button type="button" className="btn btn-primary" onClick={openCreate}>
              {t('vehicles.create')}
            </button>
          ) : undefined
        }
      />

      {vehicles.isError && (
        <div className="alert alert-danger">{(vehicles.error as Error).message}</div>
      )}

      <Card>
        <ParkingDataTable
          storageKey="vehicles"
          columns={columns}
          refreshKey={rows.map((v) => v.id).join('|')}
          actionsColumn={canWrite}
          galleryItems={rows.map((v) => ({
            id: v.id,
            icon: 'bi-car-front',
            iconTone: 'primary' as const,
            title: v.plate,
            subtitle: `${v.brand ?? '—'} ${v.model ?? ''}`.trim(),
            meta: `${v.color ?? '—'} · ${enumLabel(t, 'vehicleType', v.type)} · ${v.customerIds?.length ?? 0} customers`,
            actions: canWrite ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-primary"
                  aria-label="edit"
                  onClick={() => openEdit(v)}
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-danger"
                  aria-label="delete"
                  onClick={() => {
                    if (window.confirm(t('vehicles.confirmDelete'))) {
                      deleteVehicle.mutate(v.id);
                    }
                  }}
                >
                  <i className="bi bi-trash" />
                </button>
              </>
            ) : undefined,
          }))}
        >
          {rows.map((v) => (
            <tr key={v.id}>
              <td className="fw-semibold">{v.plate}</td>
              <td>{v.brand ?? '—'}</td>
              <td>{v.model ?? '—'}</td>
              <td>{v.color ?? '—'}</td>
              <td>{enumLabel(t, 'vehicleType', v.type)}</td>
              <td>{v.customerIds?.length ?? 0}</td>
              {canWrite && (
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-primary"
                    aria-label="edit"
                    onClick={() => openEdit(v)}
                  >
                    <i className="bi bi-pencil" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-danger"
                    aria-label="delete"
                    onClick={() => {
                      if (window.confirm(t('vehicles.confirmDelete'))) {
                        deleteVehicle.mutate(v.id);
                      }
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={dialog}
        onClose={closeDialog}
        title={editingId ? t('vehicles.edit') : t('vehicles.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={closeDialog}>
              {t('vehicles.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saveVehicle.isPending}
              onClick={() => saveVehicle.mutate()}
            >
              {t('vehicles.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          <div>
            <label className="form-label">{t('vehicles.plate')}</label>
            <input
              className="form-control"
              value={form.plate}
              onChange={(e) => setForm((f) => ({ ...f, plate: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="form-label">{t('vehicles.brand')}</label>
            <input
              className="form-control"
              value={form.brand}
              onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('vehicles.model')}</label>
            <input
              className="form-control"
              value={form.model}
              onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('vehicles.color')}</label>
            <input
              className="form-control"
              value={form.color}
              onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('vehicles.type')}</label>
            <select
              className="form-select"
              value={form.type}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  type: e.target.value as NonNullable<CreateVehicleInput['type']>,
                }))
              }
            >
              {['CAR', 'MOTORCYCLE', 'VAN', 'BUS', 'TRUCK', 'OTHER'].map((v) => (
                <option key={v} value={v}>
                  {enumLabel(t, 'vehicleType', v)}
                </option>
              ))}
            </select>
          </div>
          {!editingId && canReadCustomers && (
            <div>
              <label className="form-label">{t('vehicles.linkCustomer')}</label>
              <select
                className="form-select"
                value={form.customerId}
                onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
              >
                <option value="">—</option>
                {(customers.data?.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} — {c.firstName} {c.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </AppModal>
    </>
  );
}
